import cookie from "@utils/cookie";
import { merge } from "lodash";
import { AddressOptionalLatLng, AddressV2 } from "types/addresses";
import { Subset } from "types/utils";

import { dpApiV2, normalizeData, NormalizedData } from "./apiV2";
import { CharityCategory } from "./categories";
import { Consent } from "./consent";
import { DropPoint, DropPointMatchingSearch } from "./droppoints";

export type CollectionService = {
  "enabled": boolean;
  "phone": string;
}

export type DropoffService = {
  "enabled": boolean;
  "contactFirst": boolean;
}

export type PostalServiceBooleans = {
  "dhl_inbound_enabled": boolean;
  "dhl_outbound_enabled": boolean;
  "enabled": boolean;
  "hermes_inbound_enabled": boolean;
  "hermes_outbound_enabled": boolean;
}

export type PostalServiceStrings = {
  "delivery_instructions": string;
  "dhl_account_number": string;
}

export type PostalService = PostalServiceBooleans & PostalServiceStrings & { id: number }

export type CharitySummary = {
  "id": number;
  "name": string;
  "slug": string;
}

export type CharityDetails = {
  "id": number;
  "slug": string;
  "name": string;
  "live": string;
  "email": string;
  "phone": string;
  "website": string;
  "last_inbound_order": string;
  "last_outbound_order": string;
}

export type CharityStringFields = {
  "charity_no": string;
  "company_no": string;
  "org_intro": string;
  "name": string;
  "slug": string;
  "logo": string;
  "logo_w30": string;
  "logo_w40": string;
  "logo_w90": string;
  "logo_w140": string;
  "picture": string;
  "blurb": string;
  "contact_email": string;
  "phone": string;
  "phone2": string;
  "phone3": string;
  "website": string;
}

export type Charity = CharityStringFields & {
  "address": AddressV2;
  "id": number;
  "categories": CharityCategory[];
  "items": { name: string; tier: number; id: number; item: number; charity: number; }[];
  "live": boolean;
  "active": boolean;
  "collection_service": CollectionService,
  "dropoff_service": DropoffService,
  "postal_service": PostalService,
  "droppoints": DropPoint[],
  "verified": boolean;
  "suspended": boolean;
  "is_retailer": boolean;
  "owner": number;
  "owner_v2": number;
  "consent": Consent;
}

export type AllCharitiesCharity = {
  "charity_no": string;
  "company_no": string;
  "name": string;
  "id": number;
  "slug": string;
  "email": string;
  "phone": string;
  "last_inbound_order": string;
  "last_outbound_order": string;
  "live": boolean;
  "website": string;
}

export type ActiveCharity = {
  "name": string;
  "id": number;
  "slug": string;
  "logo_w30": string;
  "logo_w40": string;
  "logo_w90": string;
  "logo_w140": string;
  "num_items": number,
  "categories": string[]
}

export type CharityMatchingSearch = {
  id: number;
  name: string;
  slug: string;
  picture: string;
  logo_w30: string; // These don't really exist here, this is a bit of a hack
  logo_w40: string; // These don't really exist here, this is a bit of a hack
  logo_w90: string; // These don't really exist here, this is a bit of a hack
  logo_w140: string; // Only this one exists because it is needed
  blurb: string;
  postal_service: PostalService;
  dropoff_service: DropoffService;
  collection_service: CollectionService;
  droppoints: DropPointMatchingSearch[];
  items: { name: string; tier: number; }[];
  distance?: number;
}

export type FormUpdateCharityRequest = {
  id: number;
  formData?: FormData;
};

type ValidateOrgNameRequest = {
  name: string,
}

type ValidateDetailsResponse = {
  error?: string,
  message?: string,
}

type CreateOrgRequest = {
  name: string,
  is_retailer: boolean,
  charity_no?: string,
  company_no?: string,
  org_intro?: string,
  owner: {
    first_name: string,
    last_name: string,
    email: string,
    password: string,
  },
  postal_service: {
    enabled: boolean;
  }
  address: AddressOptionalLatLng,
  consent: {
    newsletter_and_updates?: boolean;
    ethical_brand_offers?: boolean;
    update_notifications?: boolean;
  },
  donate_to_charity?: boolean,
  eco_friendly_products?: boolean
}

type CreateOrgResponse = {
  id: number;
  access_token: string;
  refresh_token: string;
}

const extendedApi = dpApiV2.injectEndpoints({
  endpoints: builder => ({
    getActiveCharities: builder.query<NormalizedData<ActiveCharity>, string>({
      query: () => ({
        url: "charities/public/",
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return normalizeData(responseJson);
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Charity" ]
    }),
    getAllCharities: builder.query<NormalizedData<AllCharitiesCharity>, void>({
      query: () => ({
        url: "charities/",
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return normalizeData(responseJson);
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Charity" ]
    }),
    getAllCharitySummaries: builder.query<NormalizedData<CharitySummary>, void>({
      query: () => ({
        url: "charities/summaries/",
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return normalizeData(responseJson);
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Charity" ]
    }),
    getAllCharityDetails: builder.query<NormalizedData<CharityDetails>, void>({
      query: () => ({
        url: "charities/details/",
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return normalizeData(responseJson);
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Charity" ]
    }),
    updateCharity: builder.mutation<Charity, Subset<Charity> & {
      id: number
    }>({
      query: body => ({
        url: `charities/${body.id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: [ "Charity" ],
      async onQueryStarted(
        { ...patch },
        { dispatch, queryFulfilled, getState }
      ) {
        const patchResults = [];
        for (const {
          endpointName,
          originalArgs
        } of extendedApi.util.selectInvalidatedBy(getState(), [
            { type: "Charity" }
          ])) {
          if (![ "getCharityByOwnerId", "getCharityById" ].includes(endpointName)) continue;
          const patchResult = dispatch(
            extendedApi.util.updateQueryData(
              endpointName as ("getCharityByOwnerId" | "getCharityById"),
              originalArgs,
              draft => {
                merge(draft, {
                  ...patch
                });
              }
            )
          );
          patchResults.push(patchResult);
        }
        try {
          await queryFulfilled;
        } catch {
          for (const patchResult of patchResults) {
            patchResult.undo();
          }
        }
      }
    }),
    updateCharityPostalService: builder.mutation<PostalService, Subset<PostalService> & {
      id: number
    }>({
      query: body => ({
        url: `charities/postal_service/${body.id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: [ "Charity" ],
      async onQueryStarted(
        { ...patch },
        { dispatch, queryFulfilled, getState }
      ) {
        const patchResults = [];
        for (const {
          endpointName,
          originalArgs
        } of extendedApi.util.selectInvalidatedBy(getState(), [
            { type: "Charity" }
          ])) {
          if (![ "getCharityByOwnerId", "getCharityById" ].includes(endpointName)) continue;
          const patchResult = dispatch(
            extendedApi.util.updateQueryData(
              endpointName as ("getCharityByOwnerId" | "getCharityById"),
              originalArgs,
              draft => merge(draft, { postal_service: { ...patch } })
            )
          );
          patchResults.push(patchResult);
        }
        try {
          await queryFulfilled;
        } catch {
          for (const patchResult of patchResults) {
            patchResult.undo();
          }
        }
      }
    }),
    updateCharityCollectionService: builder.mutation<PostalService, Subset<CollectionService> & {
      id: number
    }>({
      query: body => ({
        url: `charities/collection_service/${body.id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: [ "Charity" ],
      async onQueryStarted(
        { ...patch },
        { dispatch, queryFulfilled, getState }
      ) {
        const patchResults = [];
        for (const {
          endpointName,
          originalArgs
        } of extendedApi.util.selectInvalidatedBy(getState(), [
            { type: "Charity" }
          ])) {
          if (![ "getCharityByOwnerId", "getCharityById" ].includes(endpointName)) continue;
          const patchResult = dispatch(
            extendedApi.util.updateQueryData(
              endpointName as ("getCharityByOwnerId" | "getCharityById"),
              originalArgs,
              draft => merge(draft, { collection_service: { ...patch } })
            )
          );
          patchResults.push(patchResult);
        }
        try {
          await queryFulfilled;
        } catch {
          for (const patchResult of patchResults) {
            patchResult.undo();
          }
        }
      }
    }),
    updateCharityDropoffService: builder.mutation<PostalService, Subset<DropoffService> & {
      id: number
    }>({
      query: body => ({
        url: `charities/dropoff_service/${body.id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: [ "Charity" ],
      async onQueryStarted(
        { ...patch },
        { dispatch, queryFulfilled, getState }
      ) {
        const patchResults = [];
        for (const {
          endpointName,
          originalArgs
        } of extendedApi.util.selectInvalidatedBy(getState(), [
            { type: "Charity" }
          ])) {
          if (![ "getCharityByOwnerId", "getCharityById" ].includes(endpointName)) continue;
          const patchResult = dispatch(
            extendedApi.util.updateQueryData(
              endpointName as ("getCharityByOwnerId" | "getCharityById"),
              originalArgs,
              draft => merge(draft, { dropoff_service: { ...patch } })
            )
          );
          patchResults.push(patchResult);
        }
        try {
          await queryFulfilled;
        } catch {
          for (const patchResult of patchResults) {
            patchResult.undo();
          }
        }
      }
    }),
    formUpdateCharity: builder.mutation<
      Charity,
      FormUpdateCharityRequest
    >({
      query: payload => {
        return {
          url: `charities/${payload.id}/`,
          method: "PATCH",
          headers: {
            "Content-Type": "multipart/form-data;"
          },
          body: payload.formData
        };
      },
      invalidatesTags: [ "Charity" ]
    }),
    getCharityByOwnerId: builder.query<Charity, number>({
      query: ownerId => ({
        url: `charities/${ownerId}/by_owner_id/`,
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return responseJson;
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Charity" ]
    }),
    getCharityById: builder.query<Charity, number>({
      query: charityId => ({
        url: `charities/${charityId}/`,
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return responseJson;
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Charity" ]
    }),
    getPublicCharityById: builder.query<Charity, number>({
      query: charityId => ({
        url: `charities/public/${charityId}`,
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return responseJson;
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Charity" ]
    }),
    getCharityBySlug: builder.query<Charity, string>({
      query: charitySlug => ({
        url: `charities/public/${charitySlug}/by_slug/`,
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return responseJson;
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Charity" ]
    }),
    getCharitiesMatchingString: builder.query<NormalizedData<CharityMatchingSearch>, { searchString: string, location?: { lat: number, lng: number } }>({
      query: ({ searchString, location }) => {
        let url = `charities/public/matching_search/${searchString}`;
        if (location?.lat && location?.lng) {
          url += `?lat=${location.lat}&lng=${location.lng}`;
        }
        return {
          url,
          responseHandler: async response => {
            if (response.ok) {
              const responseJson = await response.json();
              return normalizeData(responseJson);
            }
            // Just return the error data
            return await response.json();
          }
        };
      },
      providesTags: [ "Charity" ]
    }),
    validateOrgName: builder.query<ValidateDetailsResponse, ValidateOrgNameRequest>({
      query: body => ({
        url: "charities/validate-org-name/",
        method: "POST",
        body
      }),
    }),
    createOrg: builder.mutation<CreateOrgResponse, CreateOrgRequest>({
      query: body => ({
        url: "charities/public/",
        method: "POST",
        responseHandler: async response => {
          if (response.ok) {
            const responseJson: CreateOrgResponse = await response.json();
            cookie.set("jwt", responseJson.access_token, 1);
            cookie.set("jwt-refresh", responseJson.refresh_token, 72);
            return responseJson;
          }
          // Just return the error data
          return await response.json();
        },
        body
      }),
      invalidatesTags: (result, error) => error ? [] : [ "User" ]
    })
  }),
  overrideExisting: true
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetActiveCharitiesQuery,
  useGetAllCharitiesQuery,
  useGetAllCharitySummariesQuery,
  useGetAllCharityDetailsQuery,
  useGetCharityByOwnerIdQuery,
  useGetCharityByIdQuery,
  useGetPublicCharityByIdQuery,
  useGetCharityBySlugQuery,
  useUpdateCharityMutation,
  useFormUpdateCharityMutation,
  useUpdateCharityPostalServiceMutation,
  useUpdateCharityCollectionServiceMutation,
  useUpdateCharityDropoffServiceMutation,
  useGetCharitiesMatchingStringQuery,
  useLazyValidateOrgNameQuery,
  useCreateOrgMutation
} = extendedApi;

export default extendedApi;